import { useCallback, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import MKBox from 'components/MaterialKit/MKBox';
import MKTypography from 'components/MaterialKit/MKTypography';
import Button from 'components/Button';
import InputField from 'components/InputField';
import { updateDynamicTableRow, getDynamicTableRows } from 'api/sections';
import { handleErrorResponse } from 'utils/general';
import { useAuth } from 'contexts/auth';
import { getLocaleMap } from 'utils/locales';
import startCase from 'lodash/startCase';
import { getData } from 'api/general';
import { copyFingerprint } from 'api/ams';
import { sectionValueCache } from 'pages/Universal';

const AMSCopyFingerprintSection = ({ update_success_message, back_button_props, save_button_props, use_select, select_attributes, section, next_page_path, ...props }) => {
  const [lm, setLm] = useState({});
  useEffect(() => {
    getLocaleMap(['labels_edit_fingerprint_master', 'labels_algorithm', 'errors_required', 'errors_password_not_match', 'save_button', 'cancel_button', 'back_button'])
      .then((response) => {
        setLm(response);
      });
  }, []);
  const navigate = useNavigate();
  const [instanceOptions, setInstanceOptions] = useState([]);
  const { auth, setAuth } = useAuth();

  const onClickCancel = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const onSubmit = useCallback((values) => {
    const selectedIds = sectionValueCache[auth?.user?.username]?.selected_ids;
    if (values && selectedIds) {
      const postBody = {
        fingerprint_id: selectedIds[0],
        new_instance_id: values.instance,
      };
      navigate(next_page_path);
      return copyFingerprint(postBody)
        .then(() => {
          // eslint-disable-next-line no-alert
          alert(update_success_message);
        })
        .catch((err) => {
          handleErrorResponse(err, setAuth);
        });
    }
  }, [auth?.user?.username, navigate, next_page_path, setAuth, update_success_message]);

  const fetchInstanceFromApi = useCallback(() => {
    const params = {
      'name[nin]': 'Central',
    }
    return getData('/instances', params, 'bearer')
      .then(({ data }) => {
        setInstanceOptions(data.map((datum) => ({
          label: datum.name,
          value: datum.instance_id,
        })));
      });
  }, []);

  const initialValues = {
    instance: '',
  };

  useEffect(() => {
    fetchInstanceFromApi();
  }, [fetchInstanceFromApi]);

  useEffect(() => {
    const username = auth?.user?.username;
    if (!sectionValueCache[username].selected_ids || sectionValueCache[username].selected_ids.length !== 1) {
      navigate('/finger-print');
    }
  }, [auth?.user?.username, navigate]);

  return (
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      enableReinitialize
      {...props}
    >
      {({ handleChange, setFieldValue, handleBlur, handleSubmit, errors, values, isSubmitting, dirty }) => {
        return (
          <MKBox component="form" role="form" onSubmit={handleSubmit}>
            <MKBox>
              <MKTypography variant="body1" color="#000" size="medium">
                {`Audio Fingerprint Master [${props.value_1}] > Copy To Another Instance`}
              </MKTypography>
            </MKBox>
            <Grid container justifyContent="flex-end">
              <Grid item xs={12}>
                <MKTypography variant="caption" color="error">
                  {errors.form}
                  &nbsp;
                </MKTypography>
                <MKBox display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
                  <Button
                    variant="text"
                    fullWidth
                    size="medium"
                    fontSize="16px"
                    onClick={onClickCancel}
                    iconOnly={false}
                    circular={false}
                    borderRadius={0}
                    {...back_button_props}
                  >
                    {dirty ? lm.cancel_button : lm.back_button}
                  </Button>
                  <Button
                    type="submit"
                    variant="text"
                    fullWidth
                    size="medium"
                    fontSize="16px"
                    onClick={() => { onSubmit(); }}
                    iconOnly={false}
                    circular={false}
                    disabled={isSubmitting || !dirty}
                    borderRadius={0}
                    {...save_button_props}
                  >
                    {lm.save_button}
                  </Button>
                </MKBox>
              </Grid>
            </Grid>
            <MKBox pt="2rem">
              <Grid container justifyContent="center" alignItems="center">
                <Grid item xs={12} md={3} xl={2}>
                  <MKTypography
                    variant="body1"
                    color="black"
                    fontSize="16px"
                  >
                    Instance *
                  </MKTypography>
                </Grid>
                <Grid item xs={12} md={9} xl={10}>
                  <InputField
                    name="instance"
                    label="Instance"
                    value={values.instance}
                    type="select"
                    options={instanceOptions}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    handleBlur={handleBlur}
                  />
                </Grid>
              </Grid>
            </MKBox>
          </MKBox>
        );
      }}
    </Formik>
  );
};

AMSCopyFingerprintSection.propTypes = {
  next_page_path: PropTypes.string,
  update_success_message: PropTypes.string,
  back_button_props: PropTypes.object,
  save_button_props: PropTypes.object,
  label_1: PropTypes.string,
  label_2: PropTypes.string,
  label_3: PropTypes.string,
  value_1: PropTypes.string,
  value_2: PropTypes.string,
  value_3: PropTypes.string,
  section: PropTypes.shape({
    dyn_t: PropTypes.string,
    section_definition: PropTypes.shape({
      collection_definition: PropTypes.shape({
        collection_definition_id: PropTypes.string,
        attributes: PropTypes.array,
      }),
    }),
  }),
  use_select: PropTypes.bool,
  select_attributes: PropTypes.object,
};

export default AMSCopyFingerprintSection;
